/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import IziFin1 from '/asset/izifin/Layer_1-1.png';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

const Logo = localStorage.getItem('coverImage');
const CompanyName = import.meta.env.VITE_COMPANY_NAME;

const formatWithDecimals = (amount: number): string =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const splitHeroAmount = (amount: number) => {
  const formatted = formatWithDecimals(amount);
  const dot = formatted.lastIndexOf('.');
  return { main: formatted.slice(0, dot), decimal: formatted.slice(dot) };
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  const date = d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} | ${time}`;
};

const statusStyle = (status: string): string => {
  const s = String(status).toLowerCase();
  if (s === 'paid' || s === 'paidwithbnpl') return 'text-green-600';
  if (s === 'pending') return 'text-amber-500';
  return 'text-red-500';
};

const ReceiptCard = forwardRef<HTMLDivElement, { orderDetails: any }>(
  ({ orderDetails }, ref) => {
    const { miscellanousFeeName } = useSelector(
      (state: RootState) => state.purchase
    );
    const heroAmount = orderDetails
      ? splitHeroAmount(orderDetails.totalAmountWithVAT)
      : null;

    return (
      <div
        ref={ref}
        className="bg-white rounded-2xl max-w-[480px] mx-auto p-6 shadow-sm"
      >
        {/* Company header */}
        <div className="flex items-center gap-3 mb-5">
          {Logo ? (
            <img
              src={Logo}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              alt="logo"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-500 text-xl leading-none">✦</span>
            </div>
          )}
          <span className="font-bold text-[18px] text-gray-900 font-dmsans">
            {CompanyName}
          </span>
        </div>

        {/* Hero amount */}
        <div className="mb-5">
          <span className="text-[40px] font-bold text-gray-900 font-dmsans leading-tight">
            {heroAmount?.main}
          </span>
          <span className="text-[28px] font-normal text-gray-400 font-dmsans leading-tight">
            {heroAmount?.decimal}
          </span>
        </div>

        <hr className="border-gray-100 mb-4" />

        {/* Customer Details */}
        <div className="flex items-start divide-x divide-gray-200 mb-4 gap-0">
          <div className="pr-4">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Customer Name
            </p>
            <p className="text-[13px] font-medium text-gray-900 font-dmsans">
              {`${orderDetails?.lastName} ${orderDetails?.firstName}`}
            </p>
          </div>
        </div>

        <hr className="border-gray-100 mb-4" />

        {/* Invoice meta */}
        <div className="flex items-start divide-x divide-gray-200 mb-4 gap-0">
          <div className="pr-4">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Invoice No
            </p>
            <p className="text-[13px] font-medium text-gray-900 font-dmsans">
              {orderDetails.purchaseCode}
            </p>
          </div>
          <div className="px-4 flex-1 min-w-0">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Issued on
            </p>
            <p className="text-[13px] font-medium text-gray-900 font-dmsans break-words">
              {orderDetails.paidAt ? formatDate(orderDetails.paidAt) : 'N/A'}
            </p>
          </div>
          <div className="pl-4">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Payment Status
            </p>
            <p
              className={`text-[13px] font-medium font-dmsans ${statusStyle(orderDetails.paymentStatus)}`}
            >
              {orderDetails.paymentStatus}
            </p>
          </div>
        </div>

        <div className="flex items-start divide-x divide-gray-200 mb-4 gap-0">
          <div className="pr-4">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Order Type
            </p>
            <p className="text-[13px] font-medium text-gray-900 font-dmsans">
              {orderDetails.orderMode === 0 ||
              orderDetails.orderMode === 'Pickup'
                ? 'Pickup'
                : 'Delivery'}
            </p>
          </div>
          {(orderDetails.orderMode === 1 ||
            orderDetails.orderMode === 'Delivery') &&
            orderDetails.deliveryAddress && (
              <div className="pl-4 flex-1 min-w-0">
                <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
                  Delivery Address
                </p>
                <p className="text-[13px] font-medium text-gray-900 font-dmsans break-words">
                  {orderDetails.deliveryAddress}
                </p>
              </div>
            )}
        </div>

        <hr className="border-gray-100 mb-5" />

        {/* Items table */}
        <div>
          <h3 className="font-semibold text-[15px] text-gray-900 font-dmsans mb-3">
            Ordered Item(s)
          </h3>
          <table className="w-full font-dmsans">
            <thead>
              <tr>
                <th className="text-left text-[13px] font-normal text-gray-500 pb-2.5">
                  Line Item
                </th>
                <th className="text-center text-[13px] font-normal text-gray-500 pb-2.5">
                  Quantity
                </th>
                <th className="text-right text-[13px] font-normal text-gray-500 pb-2.5">
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {orderDetails?.cartItems?.map((item: any, i: number) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-2.5 text-[13px] text-gray-900">
                    {item.name}
                    {item.sku && (
                      <div className="text-[11px] text-gray-500">
                        SKU: {item.sku}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 text-[13px] text-gray-900 text-center">
                    {item.quantity}
                  </td>
                  <td className="py-2.5 text-[13px] text-gray-900 text-right">
                    {formatWithDecimals(
                      (item.unitPrice || 0) * (item.quantity ?? 1)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary box */}
        <div className="mt-4 bg-gray-50 rounded-xl p-4 font-dmsans">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-[13px] text-gray-500">Subtotal</span>
              <span className="text-[13px] text-gray-900">
                {formatWithDecimals(orderDetails.totalAmount)}
              </span>
            </div>
            {(orderDetails.vat ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">VAT</span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(orderDetails.vat)}
                </span>
              </div>
            )}
            {(orderDetails.consumptionFee ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  Consumption Fee
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(orderDetails.consumptionFee)}
                </span>
              </div>
            )}
            {(orderDetails.processingFee ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  Processing Fee
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(orderDetails.processingFee ?? 0)}
                </span>
              </div>
            )}
            {(orderDetails.miscellaneousFee ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  {miscellanousFeeName || 'Miscellanous Fee'}
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(orderDetails.miscellaneousFee ?? 0)}
                </span>
              </div>
            )}
            {orderDetails.roundingDifference != null &&
              orderDetails.roundingDifference !== 0 && (
                <div className="flex justify-between">
                  <span className="text-[13px] text-gray-500">
                    Rounding Difference
                  </span>
                  <span className="text-[13px] text-gray-900">
                    {formatWithDecimals(orderDetails.roundingDifference)}
                  </span>
                </div>
              )}
            {(orderDetails.orderMode === 1 ||
              orderDetails.orderMode === 'Delivery') && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">Delivery</span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(orderDetails.deliveryFee)}
                </span>
              </div>
            )}
          </div>
          <hr className="border-gray-200 my-3" />
          <div className="flex justify-between items-center">
            <span className="text-[14px] font-semibold text-gray-900">
              Total
            </span>
            <span className="text-[20px] font-bold text-gray-900">
              {formatWithDecimals(orderDetails.totalAmountWithVAT)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-100">
          <img src={IziFin1} className="w-5 h-5 object-contain" alt="iziFin" />
          <span className="text-[11px] text-gray-400 font-dmsans">
            Powered By iziFin
          </span>
        </div>
      </div>
    );
  }
);

ReceiptCard.displayName = 'ReceiptCard';

export default ReceiptCard;
